import React from 'react';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import { ArrowLeft, FileText, Scale, Shield, MonitorSmartphone } from 'lucide-react';
import { Link } from 'react-router-dom';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Allgemeine Geschäftsbedingungen (AGB)"
        description="Allgemeine Geschäftsbedingungen von Arabicroots - Rechtliche Bestimmungen für unsere Online-Arabischkurse."
        keywords="AGB, Allgemeine Geschäftsbedingungen, Arabicroots, Rechtliches, Nutzungsbedingungen"
        url="/agb"
        noIndex={true}
      />

      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center group">
              <img
                src="/LogoHeader4.png"
                alt="Arabicroots Logo"
                className="h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]"
              />
            </Link>
            <Link
              to="/"
              className="flex items-center text-gray-600 hover:text-[#970000] transition-colors duration-200"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Zurück zur Startseite
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white">
          {/* Title */}
          <div className="mb-12">
            <div className="flex items-center mb-4">
              <Scale className="h-12 w-12 text-[#970000] mr-4" />
              <h1 className="text-5xl font-bold text-[#970000]">Allgemeine Geschäftsbedingungen</h1>
            </div>
            <p className="text-lg text-gray-600">
              Rechtliche Bestimmungen für die Nutzung unserer Dienste
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Stand: 12. Juni 2026
            </p>
          </div>

          {/* Content Sections */}
          <div className="space-y-12">
            {/* Geltungsbereich */}
            <section className="bg-gray-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <FileText className="h-6 w-6 text-[#970000] mr-3" />
                1. Geltungsbereich
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für alle Verträge zwischen
                  Arabicroots (Abdelilah Zahraoui), nachfolgend Anbieter genannt, und den Teilnehmenden
                  über Online-Arabischkurse, Live-Unterricht, Aufzeichnungen, Unterrichtsmaterialien,
                  den Schülerbereich, die Lern-App sowie sonstige damit zusammenhängende Leistungen.
                </p>
                <p>
                  Abweichende Bedingungen der Teilnehmenden werden nicht Vertragsbestandteil, es sei denn,
                  ihrer Geltung wird ausdrücklich schriftlich zugestimmt.
                </p>
              </div>
            </section>

            {/* Vertragsschluss */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">2. Vertragsschluss</h2>
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      1
                    </div>
                    <div>
                      <p className="text-sm">
                        Die Darstellung von Kursen und Leistungen auf der Website stellt kein rechtlich bindendes Angebot dar.
                        Mit dem Absenden des Anmeldeformulars gibt der Teilnehmende eine verbindliche Anfrage bzw. ein
                        Angebot auf Abschluss eines Vertrages ab.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      2
                    </div>
                    <div>
                      <p className="text-sm">
                        Der Vertrag kommt erst zustande, wenn der Anbieter die Anmeldung schriftlich per E-Mail oder WhatsApp
                        bestätigt oder dem Teilnehmenden den Zugang zum Kurs verbindlich freigibt. Eine automatische
                        Eingangsbestätigung stellt noch keine Vertragsannahme dar.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      3
                    </div>
                    <div>
                      <p className="text-sm">
                        Es besteht kein Anspruch auf Teilnahme an einem Kurs. Der Anbieter behält sich vor, Anmeldungen
                        ohne Angabe von Gründen abzulehnen, insbesondere bei begrenzter Platzanzahl, unpassendem Niveau
                        oder organisatorischen Gründen.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      4
                    </div>
                    <div>
                      <p className="text-sm">
                        Personen unter 18 Jahren können nur mit ausdrücklicher Zustimmung eines Erziehungsberechtigten
                        teilnehmen. Der Vertrag kommt in diesem Fall mit dem Erziehungsberechtigten zustande; dieser
                        schuldet die Kursgebühr. Der Anbieter kann einen Nachweis der Zustimmung verlangen.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Kursdurchführung */}
            <section className="bg-gray-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">3. Kursdurchführung</h2>
              <div className="space-y-4 text-gray-700">
                <div className="flex items-start mb-4">
                  <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                    1
                  </div>
                  <div>
                    <p className="text-sm">
                      Die Kurse finden live online zu den angegebenen Zeiten statt.
                    </p>
                  </div>
                </div>
                <div className="flex items-start mb-4">
                  <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                    2
                  </div>
                  <div>
                    <p className="text-sm">
                      Ein Einstieg in bereits laufende Kurse ist nur nach vorheriger Absprache mit dem Anbieter möglich.
                      Erfolgt der Einstieg verspätet, können verpasste Inhalte durch Aufzeichnungen, Materialien oder
                      individuelle Hinweise des Anbieters nachgeholt werden.
                    </p>
                  </div>
                </div>
                <div className="flex items-start mb-4">
                  <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                    3
                  </div>
                  <div>
                    <p className="text-sm">
                      Die Zugangsdaten, Kurslinks oder Gruppenlinks werden dem Teilnehmenden rechtzeitig vor Kursbeginn per
                      E-Mail, WhatsApp oder auf einem anderen geeigneten Kommunikationsweg übermittelt.
                    </p>
                  </div>
                </div>
                <div className="flex items-start mb-4">
                  <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                    4
                  </div>
                  <div>
                    <p className="text-sm">
                      Bei Ausfall einzelner Unterrichtseinheiten, insbesondere wegen Krankheit, technischer Störungen,
                      höherer Gewalt oder sonstiger Umstände außerhalb des Einflussbereichs des Anbieters, kann der Anbieter
                      Ersatztermine, Zusatzaufgaben, Projekte, Aufzeichnungen oder eine Verdichtung des Stoffs anbieten. Die Entscheidung über die Form der Nachholung liegt beim Anbieter.
                    </p>
                  </div>
                </div>
                <div className="flex items-start mb-4">
                  <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                    5
                  </div>
                  <div>
                    <p className="text-sm">
                      Die angegebene Kursdauer stellt einen Richtwert dar. Da der Unterricht an das Niveau, das Lerntempo
                      und den tatsächlichen Fortschritt der Gruppe angepasst wird, kann sich die tatsächliche Dauer des
                      Kurses verkürzen oder verlängern.
                    </p>
                  </div>
                </div>
                <div className="flex items-start mb-4">
                  <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                    6
                  </div>
                  <div>
                    <p className="text-sm">
                      Die Kurse starten, sobald die jeweilige Gruppe die vorgesehene Teilnehmerzahl erreicht hat.
                      Zwischen Anmeldung und Kursbeginn können daher erfahrungsgemäß etwa drei bis vier Wochen liegen;
                      hierbei handelt es sich um einen unverbindlichen Richtwert. Der verbindliche Starttermin wird
                      den Teilnehmenden rechtzeitig mitgeteilt. Kommt eine Gruppe nicht zustande, kann der Anbieter
                      den Kursbeginn verschieben, dem Teilnehmenden einen Platz in einer anderen passenden Gruppe
                      anbieten oder vom Vertrag zurücktreten. Im Fall des Rücktritts werden bereits gezahlte
                      Kursgebühren vollständig erstattet; weitergehende Ansprüche des Teilnehmenden bestehen nicht.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                    7
                  </div>
                  <div>
                    <p className="text-sm">
                      Die Live-Sessions werden aufgezeichnet und den Teilnehmenden des jeweiligen Kurses ausschließlich
                      im Schülerbereich zur Verfügung gestellt. Der Unterricht findet grundsätzlich ohne Teilnehmer-Kamera
                      statt; in den Aufzeichnungen können daher Wortbeiträge (Stimme) und ggf. der Anzeigename der
                      Teilnehmenden enthalten sein. Wer auch mit der Stimme nicht in der Aufzeichnung erscheinen möchte,
                      kann das Mikrofon deaktiviert lassen und Fragen z. B. über den Chat stellen. Eine Weitergabe oder
                      Veröffentlichung der Aufzeichnungen durch Teilnehmende ist untersagt (siehe Ziffer 9).
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Pflichten der Teilnehmenden */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">4. Pflichten der Teilnehmenden</h2>
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="space-y-4 text-gray-700">
                  <p className="font-semibold text-gray-900 mb-3">Die Teilnehmenden verpflichten sich:</p>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                        1
                      </div>
                      <span className="text-sm">
                        über die erforderliche technische Ausstattung zu verfügen, insbesondere über eine stabile Internetverbindung
                        sowie – soweit erforderlich – Mikrofon und Kopfhörer,
                      </span>
                    </div>
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                        2
                      </div>
                      <span className="text-sm">
                        pünktlich und respektvoll am Unterricht teilzunehmen,
                      </span>
                    </div>
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                        3
                      </div>
                      <span className="text-sm">
                        respektvoll mit Lehrkräften und anderen Teilnehmenden umzugehen,
                      </span>
                    </div>
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                        4
                      </div>
                      <span className="text-sm">
                        keine Zugangsdaten, Gruppenlinks, Aufzeichnungen oder Materialien an Dritte weiterzugeben,
                      </span>
                    </div>
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                        5
                      </div>
                      <span className="text-sm">
                        bei bestimmten Kursen die jeweils vorausgesetzten Vorkenntnisse mitzubringen,
                      </span>
                    </div>
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                        6
                      </div>
                      <span className="text-sm">
                        Änderungen der Kontaktdaten unverzüglich mitzuteilen.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Preise und Zahlung */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">5. Preise & Zahlungsbedingungen</h2>
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      1
                    </div>
                    <div>
                      <p className="text-sm">
                        Die Kursgebühr ergibt sich aus dem jeweiligen Kursangebot oder der individuellen Vereinbarung.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      2
                    </div>
                    <div>
                      <p className="text-sm">
                        Die Zahlung erfolgt im Voraus per Überweisung oder über ein individuell vereinbartes Zahlungsmittel.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      3
                    </div>
                    <div>
                      <p className="text-sm">
                        Soweit eine monatliche Zahlung angeboten wird, handelt es sich – sofern nicht ausdrücklich etwas anderes
                        vereinbart ist – nicht um eine monatlich kündbare Mitgliedschaft, sondern um eine Ratenzahlungsvereinbarung
                        für den gesamten gebuchten Kursabschnitt.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      4
                    </div>
                    <div>
                      <p className="text-sm">
                        Der Zugang zum Kurs, zu Gruppen und zu den Kursmaterialien erfolgt erst nach Zahlungseingang bzw. nach
                        ausdrücklicher Freigabe durch den Anbieter.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      5
                    </div>
                    <div>
                      <p className="text-sm">
                        Bei Zahlungsverzug kann der Anbieter den Zugang zu Live-Unterricht, Aufzeichnungen, Gruppen und Materialien
                        vorübergehend sperren. Die Zahlungspflicht für den gebuchten Kursabschnitt bleibt hiervon unberührt.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      6
                    </div>
                    <div>
                      <p className="text-sm">
                        Bei anhaltender Nichtzahlung werden Mahnungen verschickt. Weitere rechtliche Schritte sowie die Geltendmachung
                        gesetzlicher Verzugsansprüche bleiben vorbehalten.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      7
                    </div>
                    <div>
                      <p className="text-sm">
                        Soweit Kursdauer, monatliche Raten oder ein ungefährer Gesamtpreis auf der Website, im Kursangebot
                        oder in sonstigen Informationen als Richtwerte angegeben werden, richtet sich die tatsächlich zu zahlende
                        Vergütung nach der realen Laufzeit des Kurses. Verlängert sich der Kurs über die ursprünglich angegebene
                        Dauer hinaus, sind die entsprechenden weiteren Monatsraten bzw. Vergütungen bis zum tatsächlichen Ende
                        des Kurses weiterzuzahlen – höchstens jedoch für drei zusätzliche Monate über die angegebene Dauer hinaus.
                        Eine darüber hinausgehende kostenpflichtige Verlängerung bedarf einer gesonderten Vereinbarung mit dem
                        Teilnehmenden. Verkürzt sich der Kurs, endet die Zahlungspflicht mit dem tatsächlichen Kursende.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Verbindlichkeit der Anmeldung & Platzreservierung */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">6. Verbindlichkeit der Anmeldung & Platzreservierung</h2>
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      1
                    </div>
                    <div>
                      <p className="text-sm">
                        Mit der Anmeldung reserviert der Teilnehmende einen festen Kursplatz in einem Livekurs mit begrenzter
                        Teilnehmerzahl. Dieser Platz wird verbindlich für den Teilnehmenden eingeplant und kann nach Vertragsabschluss
                        regelmäßig nicht anderweitig vergeben werden.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      2
                    </div>
                    <div>
                      <p className="text-sm">
                        Die Anmeldung verpflichtet zur vollständigen Zahlung der vereinbarten Kursgebühr für den gebuchten Kursabschnitt,
                        auch wenn der Teilnehmende einzelne oder mehrere Live-Sessions nicht wahrnimmt oder die Teilnahme eigenständig
                        abbricht. Nichterscheinen gilt nicht als Rücktritt.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      3
                    </div>
                    <div>
                      <p className="text-sm">
                        Die Kurse finden in festen Gruppen und über klar definierte Zeitabschnitte statt. Eine ordentliche Kündigung
                        während eines bereits begonnenen und fest gebuchten Kursabschnitts ist ausgeschlossen. Das Recht zur
                        außerordentlichen Kündigung aus wichtigem Grund bleibt unberührt.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      4
                    </div>
                    <div>
                      <p className="text-sm">
                        Der Anbieter plant Personal-, Zeit- und Gruppenressourcen auf Grundlage der angemeldeten Teilnehmenden.
                        Aus diesem Grund besteht grundsätzlich kein Anspruch auf Erstattung, wenn der Teilnehmende den Kurs vorzeitig
                        abbricht oder aus persönlichen Gründen nicht teilnehmen kann.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      5
                    </div>
                    <div>
                      <p className="text-sm">
                        Am Ende des gebuchten Kursabschnitts kann der Teilnehmende entscheiden, ob er den Kurs beendet bzw. kündigt,
                        auf die nächste Stufe wechselt oder in einen neuen Kurs einsteigt.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

           {/* Widerrufsrecht */}
<section className="bg-gray-50 rounded-2xl p-8">
  <h2 className="text-2xl font-bold text-gray-900 mb-6">7. Widerrufsrecht</h2>
  <div className="space-y-4 text-gray-700">

    <div className="bg-white rounded-xl p-6 border border-gray-200">
      <h3 className="font-semibold text-gray-900 mb-3">Widerrufsrecht für Verbraucher</h3>
      <p className="text-sm mb-4">
        Verbraucher haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen.
      </p>
      <p className="text-sm mb-4">
        Die Widerrufsfrist beträgt vierzehn Tage ab dem Tag des Vertragsschlusses.
      </p>
      <p className="text-sm mb-4">
        Um Ihr Widerrufsrecht auszuüben, müssen Sie uns (Arabicroots, Abdelilah Zahraoui,
        c/o IP-Management #4911, Ludwig-Erhard-Str. 18, 20459 Hamburg, Deutschland,
        Telefon: +49 178 8378689, E-Mail: info@arabicroots.de) mittels einer eindeutigen Erklärung
        (z. B. ein mit der Post versandter Brief oder eine E-Mail) über Ihren Entschluss,
        diesen Vertrag zu widerrufen, informieren.
      </p>
      <p className="text-sm">
        Zur Wahrung der Widerrufsfrist reicht es aus, dass Sie die Mitteilung über die Ausübung des Widerrufsrechts
        vor Ablauf der Widerrufsfrist absenden.
      </p>
    </div>

    <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
      <h4 className="font-semibold text-orange-900 mb-2">Vorzeitiges Erlöschen des Widerrufsrechts</h4>
      <p className="text-sm text-orange-800 mb-2">
        Das Widerrufsrecht erlischt vorzeitig, wenn der Anbieter mit der Ausführung der Dienstleistung begonnen hat,
        nachdem der Verbraucher ausdrücklich zugestimmt hat, dass mit der Ausführung vor Ablauf der Widerrufsfrist
        begonnen wird, und seine Kenntnis davon bestätigt hat, dass er durch seine Zustimmung sein Widerrufsrecht verliert.
      </p>
      <p className="text-sm text-orange-800 mb-2">
        Dies gilt insbesondere bei der Bereitstellung digitaler Inhalte (z. B. PDFs, Videos, Kursaufzeichnungen),
        sobald der Zugriff darauf gewährt wurde.
      </p>
      <p className="text-sm text-orange-800">
        Die ausdrückliche Zustimmung hierzu kann bereits im Anmeldeformular erteilt werden. Ohne diese Zustimmung
        wird der Zugang zu Kursleistungen und digitalen Inhalten erst nach Ablauf der Widerrufsfrist freigeschaltet.
      </p>
    </div>

    <div className="bg-white rounded-xl p-6 border border-gray-200">
      <h3 className="font-semibold text-gray-900 mb-3">Folgen des Widerrufs</h3>
      <p className="text-sm mb-4">
        Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle Zahlungen, die wir von Ihnen erhalten haben,
        unverzüglich und spätestens binnen vierzehn Tagen ab dem Tag zurückzuzahlen, an dem die Mitteilung
        über Ihren Widerruf bei uns eingegangen ist.
      </p>
      <p className="text-sm mb-4">
        Für diese Rückzahlung verwenden wir dasselbe Zahlungsmittel, das Sie bei der ursprünglichen
        Transaktion eingesetzt haben, es sei denn, mit Ihnen wurde ausdrücklich etwas anderes vereinbart;
        in keinem Fall werden Ihnen wegen dieser Rückzahlung Entgelte berechnet.
      </p>
      <p className="text-sm">
        Haben Sie verlangt, dass die Dienstleistungen während der Widerrufsfrist beginnen sollen,
        so haben Sie uns einen angemessenen Betrag zu zahlen, der dem Anteil der bis zu dem Zeitpunkt,
        zu dem Sie uns von der Ausübung des Widerrufsrechts unterrichten, bereits erbrachten Dienstleistungen
        im Vergleich zum Gesamtumfang der im Vertrag vorgesehenen Dienstleistungen entspricht.
      </p>
    </div>

    <div className="bg-gray-100 rounded-xl p-6 border border-gray-200">
      <h3 className="font-semibold text-gray-900 mb-3">Muster-Widerrufsformular</h3>
      <p className="text-sm mb-2">
        (Wenn Sie den Vertrag widerrufen wollen, dann füllen Sie bitte dieses Formular aus und senden Sie es zurück.)
      </p>
      <p className="text-sm">
        An: Arabicroots, Abdelilah Zahraoui, c/o IP-Management #4911, Ludwig-Erhard-Str. 18,
        20459 Hamburg, Deutschland – E-Mail: info@arabicroots.de
      </p>
      <p className="text-sm mt-2">
        Hiermit widerrufe ich den von mir abgeschlossenen Vertrag über die Buchung eines Kurses.
      </p>
      <p className="text-sm mt-2">
        Name: __________________________
      </p>
      <p className="text-sm">
        Anschrift: ______________________
      </p>
      <p className="text-sm">
        Datum: _________________________
      </p>
      <p className="text-sm">
        Unterschrift (nur bei Mitteilung auf Papier): ____________________
      </p>
    </div>

  </div>
</section>

            {/* Rücktritt, Kündigung & Pausierung */}
<section>
  <h2 className="text-2xl font-bold text-gray-900 mb-6">8. Rücktritt, Kündigung & Pausierung</h2>
  <div className="bg-white border border-gray-200 rounded-xl p-6">
    <div className="space-y-4 text-gray-700">

      <div className="flex items-start mb-4">
        <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
          1
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">Rücktritt vor Kursbeginn</h3>
          <p className="text-sm">
            Ein Rücktritt vor Kursbeginn richtet sich nach den gesetzlichen Vorschriften, insbesondere dem gesetzlichen
            Widerrufsrecht für Verbraucher, soweit dieses anwendbar ist. Im Übrigen ist ein Rücktritt nach verbindlichem
            Vertragsschluss nur mit ausdrücklicher Zustimmung des Anbieters möglich.
          </p>
        </div>
      </div>

      <div className="flex items-start mb-4">
        <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
          2
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">Vertragslaufzeit & ordentliche Kündigung</h3>
          <p className="text-sm">
            Der Vertrag läuft über die jeweils vereinbarte Kursdauer. Eine ordentliche Kündigung während eines laufenden,
            fest gebuchten Kursabschnitts ist ausgeschlossen. Nach Ablauf des gebuchten Kursabschnitts endet der Vertrag
            automatisch, ohne dass es einer gesonderten Kündigung bedarf.
          </p>
        </div>
      </div>

      <div className="flex items-start mb-4">
        <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
          3
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">Pausierung</h3>
          <p className="text-sm">
            Eine Kursunterbrechung oder Pause ist nur nach ausdrücklicher Absprache und Zustimmung des Anbieters möglich.
            Ein Anspruch hierauf besteht nicht. Soweit der Anbieter im Einzelfall eine Pause gestattet, entscheidet er
            auch über die Bedingungen der Fortsetzung.
          </p>
        </div>
      </div>

      <div className="flex items-start">
        <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
          4
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">Außerordentliche Kündigung</h3>
          <p className="text-sm">
            Der Anbieter behält sich das Recht vor, den Vertrag bei Vorliegen eines wichtigen Grundes fristlos zu kündigen –
            insbesondere bei erheblichem Zahlungsverzug, schwerwiegenden Verstößen gegen vertragliche Pflichten oder
            missbräuchlicher Nutzung der Inhalte und Zugänge.
          </p>
        </div>
      </div>

    </div>
  </div>
</section>

            {/* Urheberrecht & Nutzungsrechte */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">9. Urheberrecht & Nutzungsrechte</h2>
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      1
                    </div>
                    <div>
                      <p className="text-sm">
                        Alle Kursunterlagen, PDFs, Videos, Aufzeichnungen und sonstigen Inhalte sind urheberrechtlich geschützt.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      2
                    </div>
                    <div>
                      <p className="text-sm">
                        Sie dürfen ausschließlich für den privaten, persönlichen Gebrauch im Rahmen des gebuchten Kurses verwendet werden.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      3
                    </div>
                    <div>
                      <p className="text-sm">
                        Eine Weitergabe, Vervielfältigung, Veröffentlichung oder sonstige Verwertung, auch auszugsweise, ist unzulässig.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      4
                    </div>
                    <div>
                      <p className="text-sm">
                        Die Weitergabe von Zugangsdaten, Gruppenlinks, internen Informationen oder Aufzeichnungen an Dritte ist strengstens verboten.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Schülerbereich & Lern-App */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <MonitorSmartphone className="h-6 w-6 text-[#970000] mr-3" />
                10. Schülerbereich & Lern-App
              </h2>
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">1</div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Zugang zum Schülerbereich</h3>
                      <p className="text-sm">
                        Mit Bestätigung der Anmeldung und nach Eingang der vereinbarten Zahlung erhalten Teilnehmende
                        Zugang zum persönlichen Schülerbereich. Dort stehen Kursaufzeichnungen, Materialien,
                        Live-Termine, Lernfortschritt und Zertifikate zur Verfügung. Der Zugang ist personengebunden
                        und nicht übertragbar.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">2</div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Zugangsdaten</h3>
                      <p className="text-sm">
                        Die Zugangsdaten (E-Mail und Passwort) sind vertraulich zu behandeln und dürfen nicht an Dritte
                        weitergegeben werden. Bei Verdacht auf Missbrauch oder unbefugten Zugriff ist der Anbieter
                        unverzüglich zu informieren. Der Anbieter behält sich vor, bei Weitergabe von Zugangsdaten
                        den Zugang fristlos zu sperren.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">3</div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Lern-App</h3>
                      <p className="text-sm">
                        Die Arabicroots Lern-App ist ausschließlich für eingeschriebene Schülerinnen und Schüler
                        verfügbar und stellt einen ergänzenden Bestandteil des gebuchten Kurses dar. Sie ermöglicht
                        das Üben von Vokabeln, das Nachschlagen im Wörterbuch sowie weitere kursbezogene Funktionen.
                        Ein eigenständiges Vertragsverhältnis entsteht durch die Nutzung der App nicht.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">4</div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Verfügbarkeit</h3>
                      <p className="text-sm">
                        Der Anbieter bemüht sich um eine zuverlässige Bereitstellung von Schülerbereich und App,
                        übernimmt jedoch keine Garantie für eine ununterbrochene Verfügbarkeit. Wartungsarbeiten,
                        technische Störungen oder Updates können zu vorübergehenden Einschränkungen führen.
                        Hieraus entsteht kein Anspruch auf Minderung der Kursgebühr.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">5</div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Zugang nach Kursende</h3>
                      <p className="text-sm">
                        Der Zugang zum Schülerbereich und zur Lern-App gilt für die Dauer des gebuchten Kursabschnitts.
                        Nach Kursende behält sich der Anbieter vor, den Zugang zu deaktivieren oder auf einen
                        eingeschränkten Lesezugriff zu reduzieren. Eine Verlängerung ist bei Buchung eines
                        Folgekurses möglich.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Haftung */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Shield className="h-6 w-6 text-[#970000] mr-3" />
                11. Haftung
              </h2>
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      1
                    </div>
                    <div>
                      <p className="text-sm">
                        Der Anbieter haftet unbeschränkt bei Vorsatz und grober Fahrlässigkeit sowie bei Verletzung von Leben, Körper oder Gesundheit.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      2
                    </div>
                    <div>
                      <p className="text-sm">
                        Bei leicht fahrlässiger Verletzung wesentlicher Vertragspflichten haftet der Anbieter nur auf den vertragstypischen,
                        vorhersehbaren Schaden.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      3
                    </div>
                    <div>
                      <p className="text-sm">
                        Der Anbieter haftet nicht für technische Störungen, die außerhalb seines Einflussbereichs liegen,
                        insbesondere Internetprobleme oder Geräteausfälle auf Seiten der Teilnehmenden.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      4
                    </div>
                    <div>
                      <p className="text-sm">
                        Ein bestimmter Lernerfolg, ein bestimmtes Sprachlevel oder ein bestimmtes Prüfungsergebnis wird nicht geschuldet.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Datenschutz */}
            <section className="bg-gray-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">12. Datenschutz</h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Personenbezogene Daten werden nur im Rahmen der geltenden Datenschutzgesetze verarbeitet. Details sind der
                  <Link to="/datenschutz" className="text-[#970000] hover:underline ml-1">
                    Datenschutzerklärung
                  </Link> zu entnehmen.
                </p>
              </div>
            </section>

            {/* Schlussbestimmungen */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">13. Schlussbestimmungen</h2>
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      1
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Änderungen</h3>
                      <p className="text-sm">
                        Änderungen und Ergänzungen individueller Vereinbarungen bedürfen mindestens der Textform.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start mb-4">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      2
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Salvatorische Klausel</h3>
                      <p className="text-sm">
                        Sollten einzelne Bestimmungen dieser AGB ganz oder teilweise unwirksam sein oder werden, bleibt die
                        Wirksamkeit der übrigen Bestimmungen unberührt.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-[#970000] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-1 flex-shrink-0">
                      3
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Anwendbares Recht und Gerichtsstand</h3>
                      <p className="text-sm">
                        Für Verbraucher gilt der gesetzliche Gerichtsstand. Dieser richtet sich nach dem Wohnsitz des Verbrauchers. Für Unternehmer ist Gerichtsstand der Sitz des Anbieters.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Kontakt */}
            <section className="bg-gradient-to-r from-[#970000] to-[#b30000] rounded-2xl p-8 text-white">
              <h2 className="text-2xl font-bold mb-6">Fragen zu den AGB?</h2>
              <p className="text-white/90 mb-6">
                Bei Fragen zu diesen Geschäftsbedingungen kontaktieren Sie uns gerne:
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="mailto:info@arabicroots.de"
                  className="bg-white text-[#970000] px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors duration-200 text-center"
                >
                  E-Mail senden
                </a>
                <Link
                  to="/impressum"
                  className="border-2 border-white text-white px-6 py-3 rounded-full font-semibold hover:bg-white hover:text-[#970000] transition-colors duration-200 text-center"
                >
                  Impressum
                </Link>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;
